// / Updated travelApi.js - Use dedicated travel endpoints
import axios from "axios"

const API_BASE_URL = "https://ims.holdthechild.net/api"

const getAuthHeaders = () => {
  const token = localStorage.getItem("token")
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }
}

// Use dedicated travel endpoints instead of mixing with requisitions
export const getTravelRequests = async (filters = {}) => {
  try {
    const params = new URLSearchParams(filters).toString()
    const res = await axios.get(`${API_BASE_URL}/travel?${params}`, { headers: getAuthHeaders() })
    return res.data
  } catch (err) {
    console.error("Error fetching travel requests:", err.response?.data || err.message)
    throw err
  }
}

// Create travel request using dedicated travel endpoint
export const createTravelRequest = async (travelData) => {
  try {
    console.log("Sending travel request data:", travelData)

    // Format data for the travel controller
    const travelRequest = {
      destination: travelData.destination?.trim() || "",
      purpose: travelData.purpose?.trim() || "",
      startDate: travelData.startDate || "",
      endDate: travelData.endDate || "",
      estimatedCost: Number.parseFloat(travelData.estimatedCost) || 0,
      totalEstimatedCost: Number.parseFloat(travelData.estimatedCost) || 0,
      urgency: travelData.urgency || "normal",
      transportMode: travelData.transportMode || "",
      accommodationType: travelData.accommodationType || "",
      description: travelData.description?.trim() || "",
      projectId: travelData.projectId || null,

      // Additional fields for compatibility
      typeOfProcurement: "travel",
      requestType: "travel",
      department: localStorage.getItem("department") || "",
      requesterName: localStorage.getItem("userName") || localStorage.getItem("name") || "",
    }

    console.log("Final travel request payload:", travelRequest)

    const res = await axios.post(`${API_BASE_URL}/travel`, travelRequest, { headers: getAuthHeaders() })

    console.log("Travel request created successfully:", res.data)
    return res.data
  } catch (err) {
    console.error("Detailed error creating travel request:", {
      status: err.response?.status,
      statusText: err.response?.statusText,
      data: err.response?.data,
      message: err.message,
    })

    const errorMessage =
      err.response?.data?.message || err.response?.data?.error || err.message || "Unknown error occurred"

    throw new Error(`Failed to create travel request: ${errorMessage}`)
  }
}

// Get single travel request
export const getTravelRequestById = async (id) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/travel/${id}`, { headers: getAuthHeaders() })
    return res.data
  } catch (err) {
    console.error(`Error fetching travel request ${id}:`, err.response?.data || err.message)
    throw err
  }
}

// Update travel request
export const updateTravelRequest = async (id, updateData) => {
  try {
    const res = await axios.put(`${API_BASE_URL}/travel/${id}`, updateData, { headers: getAuthHeaders() })
    return res.data
  } catch (err) {
    console.error(`Error updating travel request ${id}:`, err.response?.data || err.message)
    throw err
  }
}

// Approve travel request
export const approveTravelRequest = async (id, comments = "") => {
  try {
    const res = await axios.patch(`${API_BASE_URL}/travel/${id}/approve`, { comments }, { headers: getAuthHeaders() })
    return res.data
  } catch (err) {
    console.error(`Error approving travel request:`, err.response?.data || err.message)
    throw err
  }
}

// Reject travel request
export const rejectTravelRequest = async (id, comments = "") => {
  try {
    const res = await axios.patch(
      `${API_BASE_URL}/travel/${id}/reject`,
      { reason: comments, comments },
      { headers: getAuthHeaders() },
    )
    return res.data
  } catch (err) {
    console.error(`Error rejecting travel request:`, err.response?.data || err.message)
    throw err
  }
}

// Add itinerary to travel request
export const addItineraryToRequest = async (id, itineraryData) => {
  try {
    const res = await axios.post(`${API_BASE_URL}/travel/${id}/itinerary`, itineraryData, { headers: getAuthHeaders() })
    return res.data
  } catch (err) {
    console.error(`Error adding itinerary:`, err.response?.data || err.message)
    throw err
  }
}

// Submit mission report
export const submitMissionReport = async (id, reportData) => {
  try {
    const res = await axios.post(`${API_BASE_URL}/travel/${id}/mission-report`, reportData, {
      headers: getAuthHeaders(),
    })
    return res.data
  } catch (err) {
    console.error(`Error submitting mission report:`, err.response?.data || err.message)
    throw err
  }
}

// Review mission report
export const reviewMissionReport = async (reportId, comments = "") => {
  try {
    const res = await axios.patch(
      `${API_BASE_URL}/travel/mission-report/${reportId}/review`,
      { comments },
      { headers: getAuthHeaders() },
    )
    return res.data
  } catch (err) {
    console.error(`Error reviewing mission report:`, err.response?.data || err.message)
    throw err
  }
}

// Utility function to validate travel request data
export const validateTravelRequestData = (data) => {
  const errors = []

  if (!data.destination?.trim()) {
    errors.push("Destination is required")
  }

  if (!data.purpose?.trim()) {
    errors.push("Purpose is required")
  }

  if (!data.startDate) {
    errors.push("Start date is required")
  }

  if (!data.endDate) {
    errors.push("End date is required")
  }

  if (data.startDate && data.endDate) {
    const startDate = new Date(data.startDate)
    const endDate = new Date(data.endDate)

    // Check if dates are valid
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      errors.push("Invalid date format provided")
    } else if (startDate > endDate) {
      errors.push("End date must be after start date")
    }
  }

  const estimatedCost = Number.parseFloat(data.estimatedCost)
  if (!data.estimatedCost || isNaN(estimatedCost) || estimatedCost <= 0) {
    errors.push("Valid estimated cost is required")
  }

  return errors
}

// Get travel reports and analytics
export const getTravelReports = async (filters = {}) => {
  try {
    const params = new URLSearchParams(filters).toString()
    const res = await axios.get(`${API_BASE_URL}/travel/reports?${params}`, { headers: getAuthHeaders() })
    return res.data
  } catch (err) {
    console.error("Error fetching travel reports:", err.response?.data || err.message)
    throw err
  }
}
